import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesCore } from '@components/ui/Sparkles';
import { generateAttackWithFixes, AttackWithFixes } from '@services/groq';

// ─── Types ────────────────────────────────────────────────────────────────────
type GamePhase = 'intro' | 'playing' | 'victory' | 'defeat';
type FixOption = AttackWithFixes['fixes'][number];
type SkillPilar = 'memoria' | 'atencion' | 'sistema2' | 'base';

interface AttackEntry {
  id: number;
  type: string;
  payload: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  fixes: FixOption[];
  status: 'active' | 'blocked' | 'autoblocked' | 'passed';
  blockedBy?: string;
  timeLeft: number;
}

interface ComboState {
  memoria: number;
  atencion: number;
  sistema2: number;
  base: number;
}

const TOTAL_TIME = 480;
const MAX_PASSED = 3;
const ATTACKS_TO_WIN = 10;
const ATTACK_WINDOW = 45;

const PILAR_INFO: Record<SkillPilar, { icon: string; name: string; color: string }> = {
  memoria: { icon: '🔮', name: 'Memoria', color: '#ea00d9' },
  atencion: { icon: '🟡', name: 'Atención', color: '#ffdf00' },
  sistema2: { icon: '🔷', name: 'Sistema 2', color: '#00d2ff' },
  base: { icon: '🟠', name: 'Base Cognitiva', color: '#ff6b35' }
};

let attackIdCounter = 0;

export default function GatewayGame({ onExit }: { onExit: () => void }) {
  const [phase, setPhase] = useState<GamePhase>('intro');
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [attacks, setAttacks] = useState<AttackEntry[]>([]);
  const [agentRole, setAgentRole] = useState<string[]>([]);
  const [blockedCount, setBlockedCount] = useState(0);
  const [passedCount, setPassedCount] = useState(0);
  const [securityLevel, setSecurityLevel] = useState(100);
  const [loadingAttack, setLoadingAttack] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'autoblock' | 'combo'; msg: string } | null>(
    null
  );
  const [shakeScreen, setShakeScreen] = useState(false);
  const [bugPosition, setBugPosition] = useState({ x: 80, y: 20 });
  const [combo, setCombo] = useState<ComboState>({ memoria: 0, atencion: 0, sistema2: 0, base: 0 });
  const [autoBlockCount, setAutoBlockCount] = useState(0);
  const [showShield, setShowShield] = useState(false);
  const attackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousAttackTypes = useRef<string[]>([]);
  const gameActiveRef = useRef(false);
  const agentRoleRef = useRef<string[]>([]);

  // Keep ref in sync
  useEffect(() => {
    agentRoleRef.current = agentRole;
  }, [agentRole]);

  // Bug floating
  useEffect(() => {
    if (phase !== 'playing') return;
    const interval = setInterval(() => {
      setBugPosition({ x: 55 + Math.random() * 35, y: 10 + Math.random() * 50 });
    }, 3500);
    return () => clearInterval(interval);
  }, [phase]);

  // Main timer
  useEffect(() => {
    if (phase !== 'playing') return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          setPhase('defeat');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // Per-attack countdown
  useEffect(() => {
    if (phase !== 'playing') return;
    const interval = setInterval(() => {
      setAttacks((prev) => {
        let passedThisTick = 0;
        const updated = prev.map((a) => {
          if (a.status !== 'active') return a;
          const newTime = a.timeLeft - 1;
          if (newTime <= 0) {
            passedThisTick++;
            return { ...a, status: 'passed' as const, timeLeft: 0 };
          }
          return { ...a, timeLeft: newTime };
        });
        if (passedThisTick > 0) {
          setPassedCount((c) => {
            const n = c + passedThisTick;
            if (n >= MAX_PASSED) setPhase('defeat');
            return n;
          });
          setSecurityLevel((s) => Math.max(0, s - 20));
          setShakeScreen(true);
          setTimeout(() => setShakeScreen(false), 500);
        }
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // Auto-spawn attacks
  useEffect(() => {
    if (phase !== 'playing') return;
    gameActiveRef.current = true;
    const scheduleNext = () => {
      const delay = 6000 + Math.random() * 4000;
      attackTimerRef.current = setTimeout(async () => {
        if (!gameActiveRef.current) return;
        await spawnAttack();
        if (gameActiveRef.current) scheduleNext();
      }, delay);
    };
    spawnAttack();
    scheduleNext();
    return () => {
      gameActiveRef.current = false;
      if (attackTimerRef.current) clearTimeout(attackTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Win check
  useEffect(() => {
    if (blockedCount >= ATTACKS_TO_WIN && phase === 'playing') setPhase('victory');
  }, [blockedCount, phase]);

  // Auto-block check: when a new attack arrives, check if existing defenses cover it
  const tryAutoBlock = (_a: AttackEntry): boolean => {
    void _a;
    // Need at least 6 manual blocks before auto-block even has a chance
    if (agentRoleRef.current.length < 6) return false;
    // Very low chance: 10% at 6 defenses, max 15% at 8+
    const chance = Math.min(0.15, (agentRoleRef.current.length - 6) * 0.05 + 0.1);
    return Math.random() < chance;
  };

  const spawnAttack = async () => {
    setLoadingAttack(true);
    try {
      const result = await generateAttackWithFixes(agentRoleRef.current, previousAttackTypes.current);
      previousAttackTypes.current.push(result.type);
      const newAttack: AttackEntry = {
        id: ++attackIdCounter,
        type: result.type,
        payload: result.payload,
        severity: result.severity,
        fixes: result.fixes,
        status: 'active',
        timeLeft: ATTACK_WINDOW
      };

      // Check auto-block
      if (tryAutoBlock(newAttack)) {
        const matchedDefense = agentRoleRef.current[Math.floor(Math.random() * agentRoleRef.current.length)];
        newAttack.status = 'autoblocked';
        newAttack.blockedBy = matchedDefense;
        setAutoBlockCount((c) => c + 1);
        setSecurityLevel((s) => Math.min(100, s + 3));
        setFeedback({ type: 'autoblock', msg: `⚡ Auto-bloqueado por: "${matchedDefense.slice(0, 50)}..."` });
        setTimeout(() => setFeedback(null), 2500);
      }

      setAttacks((prev) => [newAttack, ...prev]);
    } catch (err) {
      console.error('Attack gen failed:', err);
    } finally {
      setLoadingAttack(false);
    }
  };

  const checkCombo = (pilar: SkillPilar, newCombo: ComboState) => {
    // Check all 4 pilars combo
    if (newCombo.memoria >= 1 && newCombo.atencion >= 1 && newCombo.sistema2 >= 1 && newCombo.base >= 1) {
      setShowShield(true);
      setSecurityLevel((s) => Math.min(100, s + 5));
      setFeedback({
        type: 'combo',
        msg: '🌟 ¡ESCUDO COGNITIVO COMPLETO! +5% seguridad — todas las habilidades activas'
      });
      setTimeout(() => {
        setShowShield(false);
        setFeedback(null);
      }, 3500);
      return;
    }
    // Check pilar combo (every 2 of same)
    if (newCombo[pilar] > 0 && newCombo[pilar] % 2 === 0) {
      setSecurityLevel((s) => Math.min(100, s + 5));
      const info = PILAR_INFO[pilar];
      setFeedback({ type: 'combo', msg: `${info.icon} ¡Combo ${info.name} x${newCombo[pilar]}! +5% seguridad` });
      setTimeout(() => setFeedback(null), 2500);
    }
  };

  const handleFix = (attackId: number, fix: FixOption) => {
    if (fix.correct) {
      setAttacks((prev) => prev.map((a) => (a.id === attackId ? { ...a, status: 'blocked', blockedBy: fix.text } : a)));
      setBlockedCount((c) => c + 1);
      setSecurityLevel((s) => Math.min(100, s + 5));
      setAgentRole((prev) => [...prev, fix.text]);

      // Update combo
      const pilar = fix.skillType as SkillPilar;
      if (pilar && PILAR_INFO[pilar]) {
        setCombo((prev) => {
          const newCombo = { ...prev, [pilar]: prev[pilar] + 1 };
          setTimeout(() => checkCombo(pilar, newCombo), 300);
          return newCombo;
        });
        const info = PILAR_INFO[pilar];
        setFeedback({ type: 'success', msg: `🛡️ Bloqueado! ${info.icon} ${info.name} añadida al prompt` });
      } else {
        setFeedback({ type: 'success', msg: '🛡️ ¡Bloqueado! Instrucción añadida al prompt.' });
      }
    } else {
      setSecurityLevel((s) => Math.max(0, s - 15));
      setShakeScreen(true);
      setTimeout(() => setShakeScreen(false), 500);
      setFeedback({ type: 'error', msg: '❌ Esa instrucción deja una brecha. Seguridad -15%' });
      if (securityLevel - 15 <= 0) setPhase('defeat');
    }
    setTimeout(() => setFeedback(null), 2500);
  };

  const startGame = () => {
    setPhase('playing');
    setTimeLeft(TOTAL_TIME);
    setAttacks([]);
    setAgentRole([]);
    setBlockedCount(0);
    setPassedCount(0);
    setSecurityLevel(100);
    setAutoBlockCount(0);
    setCombo({ memoria: 0, atencion: 0, sistema2: 0, base: 0 });
    previousAttackTypes.current = [];
    attackIdCounter = 0;
    setFeedback(null);
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const activeAttacks = attacks.filter((a) => a.status === 'active');

  // ─── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="relative min-h-[80vh] flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0">
          <SparklesCore
            id="game-sparkles"
            background="transparent"
            minSize={0.6}
            maxSize={1.4}
            particleDensity={40}
            particleColor="#ff0040"
          />
        </div>
        <motion.div
          className="absolute text-7xl z-20 select-none"
          animate={{ x: [0, 20, -10, 15, 0], y: [0, -15, 10, -5, 0], rotate: [0, 10, -10, 5, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          style={{ top: '12%', right: '12%' }}
        >
          🪲
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 text-center space-y-6 max-w-2xl"
        >
          <motion.div
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-red-500 text-sm tracking-widest"
          >
            ⚠️ SISTEMA BAJO ATAQUE ⚠️
          </motion.div>
          <h2 className="text-3xl md:text-5xl font-bold text-red-400">Sistema Comprometido</h2>
          <p className="text-gray-300 text-base leading-relaxed">
            El chatbot está recibiendo <span className="text-red-300 font-semibold">prompt injections</span> sin parar.
            Tu misión: <span className="text-white font-semibold">construir el system prompt defensivo</span>{' '}
            instrucción por instrucción. Cada defensa correcta se queda activa y{' '}
            <span className="text-green-300 font-semibold">auto-bloquea futuros ataques similares</span>. Combina
            habilidades de los 4 pilares cognitivos para activar combos.
          </p>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-left text-sm space-y-2">
            <p className="text-gray-300">
              🎯 <span className="text-white">Meta:</span> Bloquea {ATTACKS_TO_WIN} ataques manualmente
            </p>
            <p className="text-gray-300">
              ⚡ <span className="text-white">Auto-block:</span> Tus defensas bloquean ataques similares automáticamente
            </p>
            <p className="text-gray-300">
              🌟 <span className="text-white">Combos:</span> 2 del mismo pilar = +5% | Los 4 pilares = Escudo Completo
            </p>
            <p className="text-gray-300">
              💀 <span className="text-white">Pierdes si:</span> {MAX_PASSED} pasan sin respuesta o seguridad = 0%
            </p>
            <p className="text-gray-300">
              ⏱️ <span className="text-white">Timer:</span> {ATTACK_WINDOW}s por ataque / 8 min total
            </p>
          </div>
          <div className="flex gap-4 justify-center pt-4">
            <button
              onClick={startGame}
              className="px-8 py-4 rounded-xl bg-red-600 text-white font-bold text-lg hover:bg-red-500 transition-all hover:shadow-lg hover:shadow-red-500/30"
            >
              🛡️ Iniciar Defensa
            </button>
            <button
              onClick={onExit}
              className="px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white transition-all"
            >
              Salir
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ─── VICTORY ────────────────────────────────────────────────────────────────
  if (phase === 'victory') {
    return (
      <div className="relative min-h-[80vh] flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0">
          <SparklesCore
            id="victory-sparkles"
            background="transparent"
            minSize={1}
            maxSize={2}
            particleDensity={80}
            particleColor="#00ff88"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 text-center space-y-6 max-w-2xl"
        >
          <motion.h2
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-3xl md:text-5xl font-bold text-green-400"
          >
            🛡️ ¡SISTEMA BLINDADO!
          </motion.h2>
          <p className="text-lg text-green-300">
            {agentRole.length} instrucciones defensivas. {autoBlockCount} ataques auto-bloqueados.
          </p>
          <div className="flex justify-center gap-3 mb-2">
            {Object.entries(PILAR_INFO).map(([key, info]) => (
              <div
                key={key}
                className="text-center px-3 py-2 rounded-lg"
                style={{ backgroundColor: `${info.color}15`, border: `1px solid ${info.color}40` }}
              >
                <span className="text-lg">{info.icon}</span>
                <p className="text-[10px] mt-0.5" style={{ color: info.color }}>
                  {combo[key as SkillPilar]}
                </p>
              </div>
            ))}
          </div>
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-left max-h-[250px] overflow-y-auto">
            <p className="text-xs text-gray-400 mb-2">🤖 System prompt defensivo construido:</p>
            {agentRole.map((s, i) => (
              <p key={i} className="text-xs text-indigo-300 pl-2 border-l-2 border-indigo-500/40 py-0.5">
                {s}
              </p>
            ))}
          </div>
          <button
            onClick={onExit}
            className="px-8 py-4 rounded-xl bg-green-600 text-white font-bold hover:bg-green-500 transition-all"
          >
            ✓ Volver
          </button>
        </motion.div>
      </div>
    );
  }

  // ─── DEFEAT ─────────────────────────────────────────────────────────────────
  if (phase === 'defeat') {
    return (
      <div className="relative min-h-[80vh] flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0">
          <SparklesCore
            id="defeat-sparkles"
            background="transparent"
            minSize={0.8}
            maxSize={1.8}
            particleDensity={60}
            particleColor="#ff0040"
          />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative z-10 text-center space-y-6 max-w-2xl"
        >
          <motion.div
            className="text-7xl"
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 0.5, repeat: 3 }}
          >
            🪲
          </motion.div>
          <h2 className="text-3xl md:text-5xl font-bold text-red-500">Sistema Comprometido</h2>
          <p className="text-sm text-gray-400">
            Construiste {agentRole.length} defensas y auto-bloqueaste {autoBlockCount}. ¡Vuelve a intentarlo!
          </p>
          <div className="flex gap-4 justify-center pt-4">
            <button
              onClick={startGame}
              className="px-6 py-4 rounded-xl bg-red-600 text-white font-bold hover:bg-red-500 transition-all"
            >
              🔄 Reintentar
            </button>
            <button
              onClick={onExit}
              className="px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white transition-all"
            >
              Salir
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ─── PLAYING ────────────────────────────────────────────────────────────────
  return (
    <motion.div
      animate={shakeScreen ? { x: [-5, 5, -3, 3, 0] } : {}}
      transition={{ duration: 0.4 }}
      className="space-y-4 relative"
    >
      {/* Shield animation */}
      <AnimatePresence>
        {showShield && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ scale: [1, 1.3, 1], rotate: [0, 360] }}
              transition={{ duration: 1.5 }}
              className="text-[120px]"
            >
              🌟
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Bug */}
      <motion.div
        className="fixed text-5xl z-40 select-none pointer-events-none"
        animate={{
          left: `${bugPosition.x}%`,
          top: `${bugPosition.y}%`,
          rotate: [0, 15, -15, 0],
          scale: [1, 1.1, 0.95, 1]
        }}
        transition={{ duration: 3, ease: 'easeInOut' }}
      >
        <div className="relative">
          <span className="drop-shadow-[0_0_12px_rgba(255,0,0,0.9)]">🪲</span>
          <motion.div
            className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-red-400 whitespace-nowrap bg-black/80 px-1.5 py-0.5 rounded"
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            inyectando...
          </motion.div>
        </div>
      </motion.div>

      {/* Top Bar */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-black/60 border border-white/10 sticky top-0 z-30 backdrop-blur-md">
        <span className={`font-mono font-bold ${timeLeft < 30 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
          ⏱️ {formatTime(timeLeft)}
        </span>
        <div className="flex-1">
          <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
            <span>Seguridad</span>
            <span className={securityLevel < 30 ? 'text-red-400' : ''}>{securityLevel}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${securityLevel > 60 ? 'bg-green-500' : securityLevel > 30 ? 'bg-yellow-500' : 'bg-red-500'}`}
              animate={{ width: `${securityLevel}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="text-green-400">
            🛡️ {blockedCount}/{ATTACKS_TO_WIN}
          </span>
          <span className="text-cyan-400">⚡ {autoBlockCount}</span>
          <span className="text-red-400">
            💀 {passedCount}/{MAX_PASSED}
          </span>
        </div>
      </div>

      {/* Combo bar */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.02] border border-white/5">
        {Object.entries(PILAR_INFO).map(([key, info]) => {
          const count = combo[key as SkillPilar];
          return (
            <div
              key={key}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px]"
              style={{
                backgroundColor: count > 0 ? `${info.color}15` : 'transparent',
                border: count > 0 ? `1px solid ${info.color}30` : '1px solid transparent'
              }}
            >
              <span>{info.icon}</span>
              <span style={{ color: count > 0 ? info.color : '#666' }}>{count}</span>
            </div>
          );
        })}
        {combo.memoria >= 1 && combo.atencion >= 1 && combo.sistema2 >= 1 && combo.base >= 1 && (
          <span className="text-[10px] text-yellow-300 ml-auto animate-pulse">🌟 ESCUDO ACTIVO</span>
        )}
      </div>

      {/* Feedback */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-3 rounded-xl text-sm text-center ${
              feedback.type === 'success'
                ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                : feedback.type === 'autoblock'
                  ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-300'
                  : feedback.type === 'combo'
                    ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300'
                    : 'bg-red-500/10 border border-red-500/30 text-red-300'
            }`}
          >
            {feedback.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attacks */}
        <div className="lg:col-span-2 space-y-3">
          <h4 className="text-xs text-red-400 font-semibold tracking-wider flex items-center gap-2">
            🚨 PROMPTS ENTRANTES
            {activeAttacks.length > 0 && (
              <span className="bg-red-500/30 text-red-300 px-1.5 py-0.5 rounded text-[10px]">
                {activeAttacks.length}
              </span>
            )}
          </h4>

          <div className="space-y-3">
            <AnimatePresence>
              {activeAttacks.map((entry) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -40, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 40, scale: 0.9 }}
                  className="p-4 rounded-xl bg-red-500/5 border border-red-500/25"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${entry.severity === 'critical' ? 'bg-red-500/40 text-red-200' : entry.severity === 'high' ? 'bg-orange-500/40 text-orange-200' : entry.severity === 'medium' ? 'bg-yellow-500/40 text-yellow-200' : 'bg-gray-500/40 text-gray-200'}`}
                      >
                        {entry.severity.toUpperCase()}
                      </span>
                      <span className="text-sm text-red-300 font-semibold">{entry.type}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div
                        className={`w-2 h-2 rounded-full ${entry.timeLeft < 10 ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'}`}
                      />
                      <span className={`text-xs font-mono ${entry.timeLeft < 10 ? 'text-red-400' : 'text-gray-500'}`}>
                        {entry.timeLeft}s
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-3 leading-relaxed italic">&ldquo;{entry.payload}&rdquo;</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {entry.fixes.map((fix, fIdx) => (
                      <button
                        key={fIdx}
                        onClick={() => handleFix(entry.id, fix)}
                        className="text-left px-3 py-2.5 text-xs rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-indigo-500/15 hover:border-indigo-500/40 hover:text-white transition-all"
                      >
                        {fix.text}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {activeAttacks.length === 0 && !loadingAttack && (
              <div className="text-center text-gray-600 py-10 text-sm">Preparando siguiente prompt...</div>
            )}
          </div>

          {/* History */}
          {attacks.filter((a) => a.status !== 'active').length > 0 && (
            <div className="mt-4 space-y-1.5">
              <h4 className="text-[10px] text-gray-600 font-semibold">HISTORIAL</h4>
              {attacks
                .filter((a) => a.status !== 'active')
                .slice(0, 8)
                .map((entry) => (
                  <div
                    key={entry.id}
                    className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg ${entry.status === 'blocked' ? 'bg-green-500/5 text-green-400' : entry.status === 'autoblocked' ? 'bg-cyan-500/5 text-cyan-400' : 'bg-red-500/5 text-red-400'}`}
                  >
                    <span>{entry.status === 'blocked' ? '✅' : entry.status === 'autoblocked' ? '⚡' : '💀'}</span>
                    <span className="flex-1 truncate">{entry.type}</span>
                    {entry.blockedBy && (
                      <span className="text-[10px] text-gray-500 truncate max-w-[120px]">
                        {entry.status === 'autoblocked' ? 'auto' : ''}
                      </span>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Right: System Prompt */}
        <div className="space-y-3">
          <h4 className="text-xs text-indigo-400 font-semibold tracking-wider">🤖 SYSTEM PROMPT DEFENSIVO</h4>
          <div className="p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/20 max-h-[45vh] overflow-y-auto">
            {agentRole.length === 0 ? (
              <p className="text-[11px] text-gray-600 text-center py-6">Bloquea ataques para construir el prompt...</p>
            ) : (
              <div className="space-y-1.5">
                <p className="text-[10px] text-gray-500 mb-2">Instrucciones ({agentRole.length}):</p>
                {agentRole.map((instruction, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-[11px] text-indigo-300 pl-2 border-l-2 border-indigo-500/40 py-0.5"
                  >
                    {instruction}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
          <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Efectividad:</span>
              <span className="text-green-300 font-bold">
                {attacks.length > 0 ? Math.round((blockedCount / Math.max(blockedCount + passedCount, 1)) * 100) : 0}%
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Auto-blocks:</span>
              <span className="text-cyan-300 font-bold">{autoBlockCount}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
